import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Section, TeslaText as Text } from 'components';

import * as styles from './styles.module.scss';

const ThreeColumnsGfvImages = (props) => {
  const { data } = props;
  const { primary, items } = data;
  const { titlelarge } = primary;

  const cardsRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const cardCount = items.length;

  const updateActiveIndex = useCallback(() => {
    const el = cardsRef.current;
    if (!el) return;
    const cardWidth = el.querySelector(`.${styles.card}`)?.offsetWidth || 280;
    const gap = 16;
    const index = Math.round(el.scrollLeft / (cardWidth + gap));
    setActiveIndex(Math.min(index, cardCount - 1));
  }, [cardCount]);

  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateActiveIndex, { passive: true });
    return () => el.removeEventListener('scroll', updateActiveIndex);
  }, [updateActiveIndex]);

  const scrollToIndex = (index) => {
    const el = cardsRef.current;
    if (!el) return;
    const card = el.querySelectorAll(`.${styles.card}`)[index];
    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  };

  const handlePrev = () => {
    const newIndex = Math.max(activeIndex - 1, 0);
    scrollToIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = Math.min(activeIndex + 1, cardCount - 1);
    scrollToIndex(newIndex);
  };

  // Desktop drag-to-scroll handlers (touch devices use native scroll + CSS snap)
  const handlePointerDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX;
    scrollLeft.current = cardsRef.current.scrollLeft;
    cardsRef.current.style.scrollSnapType = 'none';
    cardsRef.current.style.scrollBehavior = 'auto';
    cardsRef.current.style.cursor = 'grabbing';
  };

  const handlePointerMove = (e) => {
    if (!isDragging.current) return;
    const walk = (e.pageX - startX.current) * 1.2;
    cardsRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handlePointerUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const el = cardsRef.current;
    el.style.scrollBehavior = '';
    el.style.scrollSnapType = 'x mandatory';
    el.style.cursor = '';
  };

  return (
    <Section sliceName="ThreeColumnsGfvImages" className={styles.section} containerClassName={styles.container}>
      <div className={styles.header}>
        <Text {...titlelarge} className={styles.title} />
        <div className={styles.arrows}>
          <button
            className={`${styles.arrow} ${activeIndex === 0 ? styles.disabled : ''}`}
            onClick={handlePrev}
            aria-label="Previous card"
            disabled={activeIndex === 0}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            className={`${styles.arrow} ${activeIndex >= cardCount - 1 ? styles.disabled : ''}`}
            onClick={handleNext}
            aria-label="Next card"
            disabled={activeIndex >= cardCount - 1}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
      <div
        className={styles.cards}
        ref={cardsRef}
        onMouseDown={handlePointerDown}
        onMouseMove={handlePointerMove}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerUp}
      >
        {items.map((item, index) => (
          <div key={index} className={styles.card}>
            {item.image?.url && (
              <img src={item.image.url} alt={item.image.alt || ''} className={styles.image} draggable={false} />
            )}
            <Text {...item.body1} as="p" className={styles.cardBody} />
          </div>
        ))}
      </div>
    </Section>
  );
};

export default ThreeColumnsGfvImages;
